/*
  FD brand: the sofa gallery's own behaviour.

  Everything the stage needs for a FIRST PAINT is already server-rendered --
  one slide carries `is-active`, the counter carries its number, the matching
  dimension set is the only one not `hidden`. This file never builds the
  gallery, so there is no frame in which slides are stacked or blank: with
  JavaScript off the page still shows the right slide, it just cannot be paged.

  What it adds, none of which Dawn has an equivalent for:
  - the prev/next chips and the dot strip, both of which delegate to
    media-gallery.js's setActiveMedia() so the rail, the live region and the
    stage can never disagree about what is showing;
  - the position counter, kept in step by watching `is-active` rather than by
    counting clicks, so a variant change that moves the stage updates it too;
  - the colourway and chaise-side filter. Every slide, dot and thumbnail ships
    in the DOM carrying the colour and the hand it belongs to, and a change to
    either option flips `hidden` on the ones that belong to another. Nothing is
    fetched and nothing is built, so the filter cannot disagree with the
    server's first paint -- it applies the same rule to the same markup. The
    two axes are ANDed and either may be missing: an untagged image passes both
    and a product with no chaise option has nothing to fail.
  - the phone's dimensions split. The drawing and its figures share a slide on
    a desktop column and cannot on a phone, so under 750px the figures are
    given the server's second, hidden dimensions slide and the drawing keeps
    the stage to itself. It runs through the same filter for the same reason:
    `hidden` is what the counter, the dots and the stepping all read, so this
    is the only place a slide can be added to the walk without them disagreeing
    about it. With JavaScript off, or on any wider screen, the slide stays
    hidden and the combined layout is what the page has always shown.
  - the caption, which is a written sentence about the SELECTION rather than
    the slide, rebuilt from the same setting templates the server used;
  - the per-size dimension slide. Dawn re-renders the price and the pickers on
    a variant change but reconciles the media list by id only, so the slide's
    contents are switched here instead.
*/
// The fabric swatch slide (2026-10-14): which swatch photograph a colour gets.
// Pure, and outside the element guard, so node can test it on its own.
window.FdGallerySwatch = {
  pick(map, colour) {
    if (!map || !colour) return null;
    const entry = map[colour];
    return entry && typeof entry === 'object' && entry.src ? entry : null;
  },
};
if (!customElements.get('fd-gallery')) {
  customElements.define(
    'fd-gallery',
    class FdGallery extends HTMLElement {
      connectedCallback() {
        this.gallery = this.querySelector('media-gallery');
        this.list = this.querySelector('[id^="Slider-Gallery"]');
        if (!this.list) return;

        this.slides = Array.from(this.list.querySelectorAll('[data-media-id]'));
        this.dots = Array.from(this.querySelectorAll('[data-fd-gallery-dot]'));
        this.thumbs = Array.from(this.querySelectorAll('.fd-gal__railitem'));
        this.current = this.querySelector('[data-fd-gallery-current]');
        this.total = this.querySelector('[data-fd-gallery-total]');
        this.caption = this.querySelector('[data-fd-gallery-caption]');
        // Everything carrying dimension panels: the dimensions slide (or slides
        // -- a product with no Size option gets one per set) and the thumbnail
        // cells, which now show the drawings themselves and so have the same
        // per-size, per-hand wrappers to switch. Found by their own attribute,
        // not by class or by id: the two stand-in tiles borrow
        // `fd-gal__slide--dims` for its layout and hold no panels, and the id is
        // now a run (`-dims`, `-dims-2`) whose suffix test would match only the
        // first slide.
        this.dimRoots = Array.from(this.querySelectorAll('[data-fd-dim-panels]'));
        // The fabric swatch slide: one slide whose photograph follows the colour.
        try {
          const mapEl = this.querySelector('[data-fd-swatch-map]');
          this.swatchMap = mapEl ? JSON.parse(mapEl.textContent) : null;
        } catch (e) {
          this.swatchMap = null;
        }
        this.controls = [
          this.querySelector('[data-fd-gallery-prev]'),
          this.querySelector('[data-fd-gallery-next]'),
          this.querySelector('.fd-gal__count'),
          this.querySelector('.fd-gal__dots'),
        ].filter(Boolean);

        this.colour = this.dataset.selectedColour || '';
        this.chaise = this.dataset.selectedChaise || '';

        // The mobile split: under 750px the dimensions slide gives its whole
        // stage to the plate and the figures move to a slide of their own,
        // which the server ships hidden. The gate is here rather than in CSS
        // alone because the counter, the dots and the stepping all walk
        // `hidden` -- a slide hidden by CSS would still be counted and still be
        // stepped into on desktop.
        this.mqSplit = window.matchMedia('(max-width: 749px)');
        this.onSplitChange = () => this.applyFilter();
        this.mqSplit.addEventListener('change', this.onSplitChange);

        // Belt and braces on that gate. A media-query `change` rides style
        // recalculation, and a hidden or backgrounded tab produces no rendering
        // frames to recalculate in, so a viewport that crosses 750px while the
        // page is away can come back with the gate still reading the old side.
        // `resize` is the cheap re-check that does fire on reveal-with-a-new-
        // size, and it only re-filters when the query's answer actually differs
        // from the one last applied -- never on every resize event.
        this.onWinResize = () => {
          if (this.mqSplit.matches !== this.splitApplied) this.applyFilter();
        };
        window.addEventListener('resize', this.onWinResize);

        // The Instagram-style phone gallery. `igRoot` is the merchant's
        // checkbox as printed by Liquid; `ig` is that AND the 749px gate, and
        // it is recomputed by applyFilter() -- the one place that already knows
        // which side of the gate the page is on, so the two can never disagree.
        this.igRoot = this.classList.contains('fd-gal--ig');
        this.ig = false;

        // Which colourways a companion piece was photographed in. `data-
        // companion-media` is written by the server because binding state is
        // not otherwise visible from the DOM, and deriving it from photo counts
        // would mistake an unevenly photographed single-piece product for a set
        // missing its second piece.
        this.companionColours = new Set(
          this.slides
            .filter((slide) => 'companionMedia' in slide.dataset && slide.dataset.colour)
            .map((slide) => slide.dataset.colour)
        );

        this.querySelector('[data-fd-gallery-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-fd-gallery-next]')?.addEventListener('click', () => this.step(1));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.show(dot.dataset.fdGalleryDot));
        });

        // `is-active` is moved by three different owners (these chips, the rail
        // via media-gallery.js, and a variant change). Watching the attribute
        // means the counter, dots and caption follow all three for free.
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.list, { subtree: true, attributes: true, attributeFilter: ['class'] });

        // Radio changes bubble out of <variant-selects>, so one listener on the
        // owning product-info covers every picker without reaching into them.
        this.root = this.closest('product-info') || document;
        this.onOptionChange = (event) => this.optionChanged(event);
        this.root.addEventListener('change', this.onOptionChange);

        // The filter, not sync(), because the phone's specs slide is hidden
        // until this runs and the counter has to be able to see it. Everything
        // else it touches it leaves as the server left it: it applies the same
        // colour and hand rule to the same markup.
        if (this.igRoot) this.wireIg();
        this.applyFilter();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        if (this.root && this.onOptionChange) this.root.removeEventListener('change', this.onOptionChange);
        this.mqSplit?.removeEventListener('change', this.onSplitChange);
        if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
        if (this.onIgScroll) this.list.removeEventListener('scroll', this.onIgScroll);
        if (this.onIgScrollEnd) this.list.removeEventListener('scrollend', this.onIgScrollEnd);
        if (this.onIgDown) this.list.removeEventListener('pointerdown', this.onIgDown);
        if (this.onIgClick) this.list.removeEventListener('click', this.onIgClick);
        window.clearTimeout(this.igScrollTimer);
        this.priceObserver?.disconnect();
      }

      // The slides of the colourway on show. Everything that counts, numbers or
      // steps works off this list, never off the full set, or the counter would
      // read "4 / 16" on a gallery showing six.
      visibleSlides() {
        return this.slides.filter((slide) => !slide.hidden);
      }

      step(direction) {
        // Wrap-around: the set is small and finite, and a disabled chip at each
        // end reads as a fault rather than as an edge.
        const visible = this.visibleSlides();
        if (!visible.length) return;
        const at = visible.findIndex((slide) => slide.classList.contains('is-active'));
        const next = ((at === -1 ? 0 : at) + direction + visible.length) % visible.length;
        this.show(visible[next].dataset.mediaId);
      }

      show(mediaId) {
        // In ig mode the stage IS a scroll-snap strip, so switching slide means
        // scrolling it. media-gallery.setActiveMedia() would also drag the
        // WINDOW (its "scroll the gallery into view" pass) and, on a variant
        // change, re-order the list underneath the scroll offset -- both of
        // which fight a strip. The class is still the switch, so the observer
        // still runs sync() off it.
        if (this.ig) {
          const target = this.slides.find((slide) => slide.dataset.mediaId === mediaId && !slide.hidden);
          if (target) {
            this.igActivate(target);
            this.igScrollTo(target);
            return;
          }
        }
        // media-gallery.js reads the matching rail cell's data-media-position
        // without first checking that the cell is there, so a slide the rail
        // deliberately carries no cell for -- the phone-only specs slide, the
        // rail being a desktop surface -- is switched here instead of through
        // it. Nothing is lost by the shorter path: the slides are cross-faded
        // in place rather than scrolled to, so the class IS the switch, and the
        // observer still runs sync() off it. The rail's own aria-current stays
        // on the drawing's cell, which is the half of that pair it shows.
        const railed = this.querySelector(`[data-target="${mediaId}"]`);
        if (railed && this.gallery && typeof this.gallery.setActiveMedia === 'function') {
          this.gallery.setActiveMedia(mediaId, false);
          return;
        }
        this.slides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId));
      }

      sync() {
        const visible = this.visibleSlides();
        const active = visible.find((slide) => slide.classList.contains('is-active')) || visible[0];
        if (!active) return;
        if (this.current) this.current.textContent = visible.indexOf(active) + 1;
        if (this.total) this.total.textContent = visible.length;
        this.dots.forEach((dot) => {
          if (dot.dataset.fdGalleryDot === active.dataset.mediaId) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
        // A one-slide gallery has nothing to page through, and chips over it
        // would be controls that do nothing.
        this.controls.forEach((control) => {
          control.hidden = visible.length < 2;
        });
      }

      // Radios announce which option they belong to, so one listener sorts the
      // three things a change can move: the colourway on show, the sentence
      // under the stage, and the dimension drawing.
      optionChanged(event) {
        const name = event.target?.dataset?.optionName;
        if (!name) return;

        if (name === this.dataset.colourOption) {
          this.colour = event.target.value;
          this.updateSwatch();
          this.applyFilter();
        }
        // The hand moves the same markup the colour does -- a right-hand corner
        // is a different photograph, not a different caption -- so this branch
        // re-filters as well as re-wording the sentence below.
        if (name === this.dataset.chaiseOption) {
          this.chaise = event.target.value;
          this.applyFilter();
          this.updateDimensionArt();
        }
        if (name === this.dataset.colourOption || name === this.dataset.chaiseOption) this.renderCaption();
        if (name === this.dataset.sizeOption) this.updateDimensions(event);
      }

      // A slide, dot or thumbnail carries the colour it was shot in and the
      // hand it shows, or neither when it names neither (a dimensions drawing,
      // a fabric close-up) -- those stay on whatever is chosen. An element has
      // to clear BOTH axes to be on, and an axis it says nothing about it
      // cannot fail, which is also what makes a product with no chaise option
      // inert here: `this.chaise` is '' and the second test is always true.
      //
      // The two stand-in tiles are the mirror image: "render to come" is on
      // when the chosen colour has no photograph at all, and the companion tile
      // is on when a set's second piece was photographed in other colours but
      // never in this one. Both stay colour-only -- they answer questions about
      // the fabric, and a set has no chaise option to answer anything else.
      applyFilter() {
        // The phone's specs slide is the third mirror-image case, and the
        // simplest: it belongs to a viewport rather than to a colourway. The
        // class it puts on the root is what lets CSS take the table out of the
        // drawing's slide, so the two can never be out of step.
        const split = this.mqSplit.matches;
        // Recorded so the resize re-check can tell a real crossing from noise.
        this.splitApplied = split;
        this.classList.toggle('fd-gal--dimsplit', split);
        this.ig = this.igRoot && split;

        const hasRender = this.slides.some((slide) => slide.dataset.colour === this.colour);
        const short = hasRender && this.companionColours.size > 0 && !this.companionColours.has(this.colour);
        const wanted = (element) => {
          // First, and on its own: a specs slide names no colour and no hand,
          // so the tests below would pass it whatever the viewport said.
          if ('fdDimSpecs' in element.dataset) return split;
          // The swatch slide, its dot and its rail cell are on exactly when the
          // chosen colour has a swatch photograph. They name no colour, so the
          // colour test below would otherwise pass them for every colourway.
          if ('fdSwatch' in element.dataset) return !!window.FdGallerySwatch.pick(this.swatchMap, this.colour);
          if ('renderPending' in element.dataset) return !hasRender;
          if ('companionPending' in element.dataset) return short;
          const colourOk = !element.dataset.colour || element.dataset.colour === this.colour;
          const chaiseOk = !this.chaise || !element.dataset.chaise || element.dataset.chaise === this.chaise;
          return colourOk && chaiseOk;
        };

        [this.slides, this.dots, this.thumbs].forEach((group) => {
          group.forEach((element) => {
            element.hidden = !wanted(element);
          });
        });

        const visible = this.visibleSlides();
        if (!visible.length) return;
        const active = this.slides.find((slide) => slide.classList.contains('is-active'));
        if (this.ig) {
          // A colour change re-filters the same markup, so a scroll offset
          // measured against the OLD set points at another colour's photograph.
          // media-gallery has already prepended the new featured slide, so the
          // surviving active slide IS the new colour's master and this lands on
          // index 0 of the visible strip.
          const target = active && !active.hidden ? active : visible[0];
          this.igActivate(target);
          const at = visible.indexOf(target);
          this.list.scrollLeft = at > 0 ? at * this.list.clientWidth : 0;
          this.sync();
          return;
        }
        // Stepping the stage runs sync() through the observer; staying put does
        // not, so the counter is refreshed by hand in that case.
        if (!active || active.hidden) this.show(visible[0].dataset.mediaId);
        else this.sync();
      }

      // ---- Instagram-style phone gallery (2026-09-07) ----
      // Wired once when the merchant checkbox is on; every handler re-checks
      // `this.ig`, so at 750px and above the listeners are live but inert.
      wireIg() {
        // Native scroll IS the swipe. This only reads where it stopped.
        // `scrollend` where it exists, a debounced `scroll` everywhere else.
        this.onIgScroll = () => {
          if (!this.ig) return;
          window.clearTimeout(this.igScrollTimer);
          this.igScrollTimer = window.setTimeout(() => this.igSettle(), 80);
        };
        this.list.addEventListener('scroll', this.onIgScroll, { passive: true });
        if ('onscrollend' in window) {
          this.onIgScrollEnd = () => {
            if (this.ig) this.igSettle();
          };
          this.list.addEventListener('scrollend', this.onIgScrollEnd);
        }

        // A tap opens the viewer, a swipe must not: a scroll-snap drag still
        // ends in a click on some engines, so travel is measured.
        this.onIgDown = (event) => {
          this.igPoint = { x: event.clientX, y: event.clientY };
        };
        this.onIgClick = (event) => {
          if (!this.ig || !this.igPoint) return;
          const moved =
            Math.abs(event.clientX - this.igPoint.x) + Math.abs(event.clientY - this.igPoint.y);
          this.igPoint = null;
          if (moved > 8) return;
          const slide = event.target.closest('[data-media-id]');
          if (!slide) return;
          // The same exclusions fd-lightbox's lbImg() makes: nothing without a
          // picture may ask for a full-screen picture.
          if (slide.classList.contains('fd-gal__slide--specs')) return;
          if ('renderPending' in slide.dataset || 'companionPending' in slide.dataset) return;
          // The lightbox keeps its single opener. With the merchant's
          // full-screen setting off there is no opener and the tap is a no-op,
          // which is what the checkbox's info text says.
          this.querySelector('[data-fd-gallery-expand]')?.click();
        };
        this.list.addEventListener('pointerdown', this.onIgDown, { passive: true });
        this.list.addEventListener('click', this.onIgClick);

        this.wirePriceMirror();
      }

      igSettle() {
        const visible = this.visibleSlides();
        const width = this.list.clientWidth;
        if (!visible.length || !width) return;
        const at = Math.min(Math.max(Math.round(this.list.scrollLeft / width), 0), visible.length - 1);
        const slide = visible[at];
        if (!slide || slide.classList.contains('is-active')) return;
        this.igActivate(slide);
      }

      // The class is the switch; the rail's aria-current is set here rather
      // than through media-gallery for the same reason show() bypasses it.
      igActivate(slide) {
        this.slides.forEach((one) => one.classList.toggle('is-active', one === slide));
        this.thumbs.forEach((item) => {
          const button = item.querySelector('button');
          if (!button) return;
          if (item.dataset.target === slide.dataset.mediaId) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
      }

      igScrollTo(slide) {
        const visible = this.visibleSlides();
        const at = visible.indexOf(slide);
        if (at === -1) return;
        this.list.scrollTo({ left: at * this.list.clientWidth });
      }

      // Ruling 8. The buy column's block stays the single source of truth --
      // Dawn replaces its children wholesale on every variant change
      // (product-info.js updateSourceFromDestination('price')) -- and this
      // copies it across, the dock "Add - price" segment's own pattern.
      // Fail-open: no price block or no mobile title means nothing to mirror.
      wirePriceMirror() {
        const scope = this.closest('.shopify-section') || document;
        this.priceMirror = scope.querySelector('[data-fd-price-mirror]');
        // `role="status"` is what tells the source block apart from the mirror,
        // whose id also begins "price-".
        this.priceSource = scope.querySelector('[id^="price-"][role="status"]');
        if (!this.priceMirror || !this.priceSource) return;
        this.mirrorPrice();
        this.priceObserver = new MutationObserver(() => this.mirrorPrice());
        this.priceObserver.observe(this.priceSource, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      mirrorPrice() {
        const price = this.priceSource?.querySelector('.fd-price');
        if (!price || !this.priceMirror) return;
        this.priceMirror.replaceChildren(price.cloneNode(true));
      }

      // Re-point the swatch slide and its rail thumbnail at the chosen colour's
      // photograph. Dawn's updateMedia keeps this <li> (its id never changes) but
      // never refreshes it, so this is the only writer after first paint. A
      // colour with no entry leaves the old picture in place; applyFilter()
      // hides the slide in that case.
      updateSwatch() {
        const entry = window.FdGallerySwatch.pick(this.swatchMap, this.colour);
        if (!entry) return;
        const img = this.querySelector('[data-fd-swatch-img]');
        if (img) {
          img.srcset = entry.srcset || '';
          img.src = entry.src;
          img.alt = entry.alt || '';
        }
        const thumb = this.querySelector('[data-fd-swatch-thumb]');
        if (thumb) {
          thumb.src = entry.thumb || entry.src;
          thumb.alt = '';
        }
      }

      // The same sentence the server wrote, from the same setting templates --
      // this is the only place it is rebuilt, so the two cannot drift.
      renderCaption() {
        if (!this.caption) return;
        const parts = [];
        if (this.dataset.captionTemplate && this.colour) {
          parts.push(this.dataset.captionTemplate.replace('[colour]', this.colour));
        }
        if (this.dataset.captionChaiseTemplate && this.chaise && this.chaise !== this.dataset.chaiseNone) {
          parts.push(this.dataset.captionChaiseTemplate.replace('[side]', this.chaise.toLowerCase()));
        }
        this.caption.textContent = parts.join(' ');
      }

      // The dimension drawing is handed, so a chaise change swaps the art the
      // same way it swaps the photographs. The wrapper with no data-chaise is
      // the default: it covers left-hand, symmetrical, and any combination the
      // picker allows but the drawing set does not name. A panel carrying one
      // wrapper has nothing to choose between and is left alone.
      updateDimensionArt() {
        // Slide and thumbnail carry the same wrappers, so the same rule applied
        // to every root keeps the rail cell showing the hand the stage shows.
        this.dimRoots.forEach((root) => {
          root.querySelectorAll('[data-size-value]').forEach((panel) => {
            const arts = Array.from(panel.querySelectorAll('[data-fd-dim-art]'));
            if (arts.length < 2) return;
            const match = arts.find((art) => art.dataset.chaise === this.chaise);
            const shown = match || arts.find((art) => !art.dataset.chaise) || arts[0];
            arts.forEach((art) => {
              art.hidden = art !== shown;
            });
          });
        });
      }

      updateDimensions(event) {
        const name = this.dataset.sizeOption;
        const target = event.target;
        if (!name || !target || target.dataset.optionName !== name) return;

        const chosen = target.value;
        this.dimRoots.forEach((root) => {
          const panels = Array.from(root.querySelectorAll('[data-size-value]'));
          if (panels.length < 2) return;
          const match = panels.find((panel) => panel.dataset.sizeValue === chosen);
          // No set for this size: the generic "still to come" panel stands in, so
          // the slide never goes blank and never shows another size's figures.
          const fallback = panels.find((panel) => panel.dataset.sizeValue === '');
          const shown = match || fallback;
          panels.forEach((panel) => {
            panel.hidden = panel !== shown;
          });
        });
      }
    }
  );
}
